import { createAction } from '@reduxjs/toolkit';
import { updateOrder } from '../../utils/serverAPI/paypal';
import { type PaymentBreakdown } from '../../types/PaymentBreakdown';
import { getAccountId } from '../../utils/serverAPI/getAccountId';
import { selectAuthParams } from '../../selectors/index';
import { type ShippingContact, type ShippingMethod } from '../../types/Shipping';
import { logXhrError } from '../../utils/sentry';
import { type OrderItem } from '../../types/OrderItem';
import { type StoreState } from '../../types/StoreState';
import { selectPayPalPaymentData } from '../../selectors/payment';

export const updatePayPalOrderAction = createAction<UpdateOrderActionPayload>('UPDATE_PAYPAL_ORDER');

export interface UpdateOrderParams {
  paymentAmount: string;
  currency: string;
  requestShipping: boolean;
  appDefId: string;
  appInstanceId: string;
  paymentBreakdown?: PaymentBreakdown;
  shippingContact: ShippingContact;
  shippingMethods?: ShippingMethod[];
  orderItems: OrderItem[];
}
export type UpdateOrderActionPayload = UpdateOrderParams;

/**
 * Sends request to Cashier backend to update PayPal order info
 * Should be used for updating payment amount, payment breakdowns.
 * All info should be passed because it works by PUT strategy
 * @param data PayPalUpdateOrderArgs
 */
export const updatePayPalOrder =
  (params: UpdateOrderParams) =>
  async (dispatch, getState: () => StoreState): Promise<string> => {
    const state = getState();
    const { orderId, chargeId, cashierOrderId } = selectPayPalPaymentData(state);
    const accountId = getAccountId(params.appDefId, params.appInstanceId);
    const reqArgs: Parameters<typeof updateOrder>[0] = {
      accountId,
      orderId,
      currencyCode: params.currency,
      requestShipping: params.requestShipping,
      paymentAmount: params.paymentAmount,
      paymentBreakdown: params.paymentBreakdown,
      orderItems: params.orderItems,
    };

    if (chargeId && cashierOrderId) {
      reqArgs.chargeId = chargeId;
      reqArgs.cashierOrderId = cashierOrderId;
    }

    try {
      const response = await updateOrder(reqArgs, selectAuthParams(state));
      dispatch(
        updatePayPalOrderAction({
          paymentAmount: params.paymentAmount,
          currency: params.currency,
          paymentBreakdown: params.paymentBreakdown,
          shippingContact: params.shippingContact,
          shippingMethods: params.shippingMethods,
          requestShipping: params.requestShipping,
          orderItems: params.orderItems,
          appDefId: params.appDefId,
          appInstanceId: params.appInstanceId,
        }),
      );
      return response.orderId;
    } catch (e) {
      logXhrError({
        message: 'XHR: PayPal updateOrder failed',
        axiosError: e,
        fingerprint: ['updatePayPalOrder'],
        reqArgs,
      });
      throw e;
    }
  };
